import { useEffect, useRef } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  ogUrl?: string;
  twitterCard?: string;
  jsonLd?: Record<string, unknown>;
  noIndex?: boolean;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType,
  ogUrl,
  twitterCard,
  jsonLd,
  noIndex,
}: SEOHeadProps) {
  const jsonLdScriptRef = useRef<HTMLScriptElement | null>(null);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://oopbuy.cam';

    document.title = title;

    const setOrCreateMeta = (attrName: string, attrValue: string, content: string) => {
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setOrCreateMeta('name', 'description', description);
    if (keywords) setOrCreateMeta('name', 'keywords', keywords);

    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    const resolvedOgUrl = ogUrl || canonicalUrl || siteUrl;
    if (ogTitle || title) setOrCreateMeta('property', 'og:title', ogTitle || title);
    if (ogDescription || description) setOrCreateMeta('property', 'og:description', ogDescription || description);
    if (ogImage) setOrCreateMeta('property', 'og:image', ogImage);
    if (ogType) setOrCreateMeta('property', 'og:type', ogType);
    if (resolvedOgUrl) setOrCreateMeta('property', 'og:url', resolvedOgUrl);

    const resolvedTwitterCard = twitterCard || 'summary_large_image';
    setOrCreateMeta('name', 'twitter:card', resolvedTwitterCard);
    setOrCreateMeta('name', 'twitter:title', ogTitle || title);
    setOrCreateMeta('name', 'twitter:description', ogDescription || description);

    const robotsContent = noIndex ? 'noindex, nofollow' : 'index, follow';
    setOrCreateMeta('name', 'robots', robotsContent);

    if (jsonLd) {
      if (!jsonLdScriptRef.current) {
        const existingScript = document.querySelector('script[type="application/ld+json"][data-readdy-seo]');
        if (existingScript) {
          jsonLdScriptRef.current = existingScript as HTMLScriptElement;
        } else {
          const script = document.createElement('script');
          script.setAttribute('type', 'application/ld+json');
          script.setAttribute('data-readdy-seo', 'true');
          document.head.appendChild(script);
          jsonLdScriptRef.current = script;
        }
      }
      jsonLdScriptRef.current.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      if (jsonLdScriptRef.current) {
        jsonLdScriptRef.current.remove();
        jsonLdScriptRef.current = null;
      }
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogImage, ogType, ogUrl, twitterCard, jsonLd, noIndex]);

  return null;
}